import i18n from '../../i18n';
import { formatDateTime, formatNumber } from '../../i18n/format';

const HIDDEN_KEYS = new Set([
    'password',
    'pass',
    'token',
    'accessToken',
    'refreshToken',
    'privyDid',
    'privy_did',
]);

const DEPOSIT_OBJECT_KEYS = new Set(['depositAddress', 'deposit']);
const DEPOSIT_FIELD_KEYS = new Set(['address', 'qrPayload', 'qr', 'payload']);

const NUMERIC_STRING = /^-?\d+(\.\d+)?$/;
const KNOWN_LABELS: Record<string, string> = {
    available: 'available',
    availableCredits: 'available',
    reserved: 'reserved',
    reservedCredits: 'reserved',
    pending: 'pending',
    pendingUsdt: 'pending',
    pendingDeposits: 'pending',
    currency: 'currency',
    observedAt: 'updated',
    observed_at: 'updated',
    updatedAt: 'updated',
    displayName: 'displayName',
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email',
    phone: 'phone',
    status: 'status',
    wallet: 'wallet',
    balance: 'balance',
    address: 'address',
    createdAt: 'createdAt',
};

export function formatLabel(key: string) {
    if (KNOWN_LABELS[key]) {
        return i18n.t(`labels.${KNOWN_LABELS[key]}`, { ns: 'account' });
    }

    return key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function toFiniteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && NUMERIC_STRING.test(value.trim())) {
        const parsed = Number(value.trim());
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

export function formatMoney(value: unknown, fractionDigits = 2): string | null {
    const amount = toFiniteNumber(value);
    if (amount == null) return null;

    return formatNumber(amount, {
        minimumFractionDigits: 0,
        maximumFractionDigits: fractionDigits,
    });
}

export function formatValue(value: unknown): string {
    if (value == null || value === '') {
        return '—';
    }

    if (typeof value === 'boolean') {
        return i18n.t(value ? 'values.yes' : 'values.no', { ns: 'account' });
    }

    if (typeof value === 'number') {
        return formatNumber(value, { maximumFractionDigits: 6 });
    }

    if (typeof value === 'string') {
        if (/^-?\d+\.\d+$/.test(value.trim())) {
            return formatMoney(value) ?? value;
        }

        if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
            const date = new Date(value);
            if (!Number.isNaN(date.getTime())) {
                return formatDateTime(date, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                });
            }
        }

        return value;
    }

    return String(value);
}

export type WalletSummaryRow = {
    key: string;
    label: string;
    amount: string;
};

export type WalletSummaryData = {
    currency: string;
    rows: WalletSummaryRow[];
    updated: string;
};

export function getWalletSummary(wallet: Record<string, unknown>): WalletSummaryData {
    const balance = isPlainObject(wallet.balance) ? wallet.balance : wallet;
    const currency =
        pickString(balance, ['currency']) || pickString(wallet, ['currency']) || 'USDT';

    const rows: WalletSummaryRow[] = [
        { key: 'available', source: ['available', 'availableCredits', 'balance'] },
        { key: 'reserved', source: ['reserved', 'reservedCredits'] },
        { key: 'pending', source: ['pending', 'pendingUsdt', 'pendingDeposits'] },
    ]
        .map(({ key, source }) => {
            const raw = pickUnknown(balance, source) ?? pickUnknown(wallet, source);
            const amount = formatMoney(raw);
            if (amount == null) return null;
            return { key, label: formatLabel(key), amount };
        })
        .filter((row): row is WalletSummaryRow => Boolean(row));

    return {
        currency,
        rows,
        updated: formatValue(wallet.observedAt ?? wallet.updatedAt ?? wallet.observed_at ?? ''),
    };
}

function pickUnknown(source: Record<string, unknown>, keys: string[]) {
    for (const key of keys) {
        const value = source[key];
        if (value == null || value === '') continue;
        if (isPlainObject(value)) continue;
        return value;
    }

    return null;
}

export function isPlainObject(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function splitProfile(user: Record<string, unknown>) {
    const fields: Array<{ key: string; value: unknown }> = [];
    const groups: Array<{ key: string; value: Record<string, unknown> }> = [];

    Object.entries(user).forEach(([key, value]) => {
        if (HIDDEN_KEYS.has(key) || DEPOSIT_OBJECT_KEYS.has(key) || value == null || value === '') {
            return;
        }

        if (isPlainObject(value)) {
            if (key === 'wallet') return;
            groups.push({ key, value: omitDepositFields(value) });
            return;
        }

        fields.push({ key, value });
    });

    return { fields, groups: groups.filter((group) => Object.keys(group.value).length > 0) };
}

function omitDepositFields(value: Record<string, unknown>) {
    return Object.fromEntries(
        Object.entries(value).filter(([key]) => !DEPOSIT_OBJECT_KEYS.has(key) && !DEPOSIT_FIELD_KEYS.has(key))
    );
}

function pickString(source: Record<string, unknown> | null, keys: string[]) {
    if (!source) return '';

    for (const key of keys) {
        const value = source[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }

    return '';
}

function asDepositSource(value: unknown): Record<string, unknown> | null {
    if (typeof value === 'string' && value.trim()) {
        return { address: value.trim() };
    }

    return isPlainObject(value) ? value : null;
}

export function getDepositInfo(user: Record<string, unknown>) {
    const wallet = isPlainObject(user.wallet) ? user.wallet : null;
    const nested =
        asDepositSource(user.depositAddress) ||
        asDepositSource(user.deposit) ||
        asDepositSource(wallet?.depositAddress) ||
        asDepositSource(wallet?.deposit) ||
        wallet;

    const address = pickString(nested, ['address', 'depositAddress']);
    const qrPayload = pickString(nested, ['qrPayload', 'qr', 'payload']);

    return {
        address,
        qrPayload,
    };
}
